import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useEditRows, usePersistUpload, useUploadFile } from './mutations';
import { bulkUploadDataQueryKeys, useFetchBulkUploadData } from './queries';

/**
 * Status enum for bulk upload workflow
 */
export const BULK_UPLOAD_STATUS = {
    IDLE: 'idle',
    LOADING: 'loading',
    PERSIST_ERRORS: 'persist_errors',
    PERSISTING: 'persisting',
    READY: 'ready',
    SUBMITTING: 'submitting',
    UPLOADING: 'uploading',
};

/**
 * Hook for managing bulk upload workflows.
 *
 * Handles the complete flow: upload → fetch → edit → persist
 * Uses TanStack Query for server state management.
 *
 * @param {Object} config
 * @param {Object} config.api - API functions for each operation
 * @param {Function} config.api.upload - (file, params) => Promise<{ status, data: { key } }>
 * @param {Function} config.api.fetch - (key, params, signal) => Promise<{ status, data: { columns, rows, validationResults } }>
 * @param {Function} config.api.edit - (key, params, rowsToUpdate, rowsToDelete) => Promise<{ status }>
 * @param {Function} config.api.persist - (key, params) => Promise<{ status, data }>
 * @param {Object} [config.params] - Additional params passed to all API calls (e.g., { fbmAccountName: 'smalls' })
 *
 * @returns {Object} Bulk upload state and actions
 */
export function useBulkUpload({ api, params = {} }) {
    const queryClient = useQueryClient();

    const [uploadKey, setUploadKey] = useState(null);

    const [pendingEdits, setPendingEdits] = useState({});
    const [pendingDeletes, setPendingDeletes] = useState(new Set());
    const [persistResults, setPersistResults] = useState(null);

    const uploadMutation = useUploadFile({
        api,
        onSuccess: (uploadData) => {
            setUploadKey(uploadData.key);
            setPendingEdits({});
            setPendingDeletes(new Set());
            setPersistResults(null);
        },
        params,
    });

    const fetchQuery = useFetchBulkUploadData({
        api,
        params,
        uploadKey,
    });

    const editMutation = useEditRows({
        api,
        onSuccess: (_data, _variables, context) => {
            setPendingEdits({});
            setPendingDeletes(new Set());
            queryClient.invalidateQueries({
                queryKey: bulkUploadDataQueryKeys.byKey(context.uploadKey, context.params),
            });
        },
        params,
        uploadKey,
    });

    const persistMutation = usePersistUpload({
        api,
        onSuccess: (data) => {
            setPersistResults(data?.persistResults ?? null);
        },
        params,
        uploadKey,
    });

    const { data } = fetchQuery;
    const columns = data?.columns ?? [];
    const rows = data?.rows ?? [];
    const validationErrors = data?.validationResults?.errors ?? [];

    const errorsByCell = useMemo(() => {
        const map = new Map();
        validationErrors.forEach((error) => {
            const key = `${error.row}:${error.field}`;
            map.set(key, error);
        });
        return map;
    }, [validationErrors]);

    const persistErrors = persistResults?.errors ?? [];
    const persistSuccessCount = persistResults?.successCount ?? 0;

    const errorsByRow = useMemo(() => {
        const map = new Map();
        persistErrors.forEach((error) => {
            map.set(error.row, error);
        });
        return map;
    }, [persistErrors]);

    const hasPersistErrors = persistErrors.length > 0;

    const status = useMemo(() => {
        if (uploadMutation.isPending) return BULK_UPLOAD_STATUS.UPLOADING;
        if (fetchQuery.isLoading) return BULK_UPLOAD_STATUS.LOADING;
        if (editMutation.isPending || (fetchQuery.isFetching && !fetchQuery.isLoading))
            return BULK_UPLOAD_STATUS.SUBMITTING;
        if (persistMutation.isPending) return BULK_UPLOAD_STATUS.PERSISTING;
        if (hasPersistErrors) return BULK_UPLOAD_STATUS.PERSIST_ERRORS;
        if (uploadKey && data) return BULK_UPLOAD_STATUS.READY;
        return BULK_UPLOAD_STATUS.IDLE;
    }, [
        uploadMutation.isPending,
        fetchQuery.isLoading,
        fetchQuery.isFetching,
        editMutation.isPending,
        persistMutation.isPending,
        hasPersistErrors,
        uploadKey,
        data,
    ]);

    const hasUnsavedChanges = Object.keys(pendingEdits).length > 0 || pendingDeletes.size > 0;
    const hasValidationErrors = validationErrors.length > 0;
    const canPersist = status === BULK_UPLOAD_STATUS.READY && !hasUnsavedChanges && !hasValidationErrors;

    // Actions
    const uploadFile = useCallback((file) => uploadMutation.mutateAsync(file), [uploadMutation]);

    const updateCell = useCallback((rowIndex, field, value) => {
        setPendingEdits((prev) => ({
            ...prev,
            [rowIndex]: {
                ...(prev[rowIndex] ?? {}),
                [field]: value,
            },
        }));
    }, []);

    const toggleRowDelete = useCallback((rowIndex) => {
        setPendingDeletes((prev) => {
            const next = new Set(prev);
            if (next.has(rowIndex)) {
                next.delete(rowIndex);
            } else {
                next.add(rowIndex);
            }
            return next;
        });
    }, []);

    const clearPendingChanges = useCallback(() => {
        setPendingEdits({});
        setPendingDeletes(new Set());
    }, []);

    const submitChanges = useCallback(() => {
        const rowsToUpdate = Object.entries(pendingEdits).map(([rowIndex, rowData]) => ({
            data: rowData,
            rowIndex: parseInt(rowIndex, 10),
        }));

        const rowsToDelete = Array.from(pendingDeletes);

        return editMutation.mutateAsync({ rowsToDelete, rowsToUpdate });
    }, [pendingEdits, pendingDeletes, editMutation]);

    const persist = useCallback(() => persistMutation.mutateAsync(), [persistMutation]);

    const reset = useCallback(() => {
        setUploadKey(null);
        setPendingEdits({});
        setPendingDeletes(new Set());
        setPersistResults(null);
        uploadMutation.reset();
        editMutation.reset();
        persistMutation.reset();
    }, [uploadMutation, editMutation, persistMutation]);

    const refetch = useCallback(() => fetchQuery.refetch(), [fetchQuery]);

    const clearPersistResults = useCallback(() => {
        setPersistResults(null);
    }, []);

    return {
        // State
        uploadKey,
        columns,
        rows,
        validationErrors,
        errorsByCell,
        pendingEdits,
        pendingDeletes,
        status,
        hasUnsavedChanges,
        hasValidationErrors,
        canPersist,

        // Persist results
        persistErrors,
        persistSuccessCount,
        errorsByRow,
        hasPersistErrors,

        // Error states
        uploadError: uploadMutation.error,
        fetchError: fetchQuery.error,
        editError: editMutation.error,
        persistError: persistMutation.error,

        // Actions
        uploadFile,
        updateCell,
        toggleRowDelete,
        clearPendingChanges,
        submitChanges,
        persist,
        reset,
        refetch,
        clearPersistResults,
    };
}
